/* eslint-disable no-restricted-globals */
import CloudManager from "./CloudManager";
import { getDownloadURL, ref as storageRef, StorageReference, UploadMetadata, UploadTaskSnapshot } from 'firebase/storage';
import { CloudResponse, EmptyResponse } from "./CloudResponse";
import { ScreenshotProvider } from "../../shared/sync/dataTypes/ScreenshotProvider";
import { ProjectFileMetaData } from "@/shared/sync/dataTypes/ProjectFileMetaData";
import { UserDevice } from "@/shared/sync/dataTypes/UserDevice";
import * as storageFetch from "../firebase/storageFetch";
import { FirebaseWrapper } from "@/service-worker/firebase/FirebaseBaseWrapper";

const projectFilesListSuffix = "_files.json";
  
export namespace CloudUrl {
    export interface ExtendedStorageReference extends StorageReference {
        
        logsUrl(tag: string): ExtendedStorageReference | null
        baseUrl(): string;
        userBaseUrl(): ExtendedStorageReference;
        deleteAllFiles(ref: ExtendedStorageReference): Promise<void>
        deleteFile(stroageReference: ExtendedStorageReference, filePath?: string): Promise<void>;
        relativeToProjectsPath(ref: ExtendedStorageReference, deviceId?: string): string;
        exist(ref: ExtendedStorageReference): Promise<boolean>;
        create(projectsRelativePath: string, deviceId?: string): ExtendedStorageReference;
        projectIdOfProjectFilesList(ref: ExtendedStorageReference): Promise<string | null>;
        projectFilesList(projectId: string, device?: string): ExtendedStorageReference;
        deviceMetaData(device?: string): ExtendedStorageReference;
        devicesBaseUrl(): ExtendedStorageReference ;
        fontsBaseUrl(): ExtendedStorageReference ;
        deviceBaseUrl(deviceId?: string): ExtendedStorageReference ;
        projectBaseUrl(deviceId?: string): ExtendedStorageReference ;
        projectUrl(projectId: string, deviceId?: string): ExtendedStorageReference ;
        projectScreenshotUrl(projectId: string, deviceId: string): ExtendedStorageReference ;
        foldersMetaData(): ExtendedStorageReference ;
        unsafeFeedMetaData(): ExtendedStorageReference ;
        lastComponent(ref: ExtendedStorageReference): string | null;
        getDownloadUrl(ref: ExtendedStorageReference): Promise<string>;

        child(relativePath: string): ExtendedStorageReference;

        putJson(
            storageRef: StorageReference,
            jsonObject: object,
            metadata?: UploadMetadata,
            completion?: (snapshot: UploadTaskSnapshot | null, error: Error | null) => void
        ): StorageTask;

        putFile(
            storageRef: StorageReference,
            fileURL: string,
            metadata?: UploadMetadata,
            completion?: (snapshot: UploadTaskSnapshot | null, error: Error | null) => void
          ): Promise<StorageTask>;

        putFileBlob(
            storageRef: StorageReference,
            fileBlob: Blob,
            metadata?: UploadMetadata,
            completion?: (snapshot: UploadTaskSnapshot | null, error: Error | null) => void
          ): StorageTask;

        downloadFile(storageRef: StorageReference,
                fileURL: string,
                completion?: (fileURL: string | null, error: Error | null) => void): StorageTask;

        get(baseURl: ExtendedStorageReference, maxSize?: number): Promise<ArrayBuffer>;

        projectFileMetaData(
            cloudUrl: StorageReference,
            deviceId?: string
        ): Promise<CloudResponse<ProjectFileMetaData> | EmptyResponse>;
    }

    export async function getDownloadUrl(ref: ExtendedStorageReference, abortSignal?: AbortSignal): Promise<string> {
        // Check if already aborted
        if (abortSignal?.aborted) {
            throw new DOMException('Operation aborted', 'AbortError');
        }

        let url = await getDownloadURL(ref);
        
        // Check if aborted after the request
        if (abortSignal?.aborted) {
            throw new DOMException('Operation aborted', 'AbortError');
        }
        
        return url;
    }

    export function baseUrl(): string {
        const schemeName = "bazaart"; // Replace with actual app name logic
        return schemeName.replace(/\s+/g, "_");
    }

    export function userBaseUrl(): ExtendedStorageReference {
        const userId = CloudManager.shared.userId;
        return storageRef(FirebaseWrapper.FirebaseBaseWrapper.shared.storage , `/${this.baseUrl()}/cloud_storage/users/${userId}`) as ExtendedStorageReference;
    }

    export function devicesBaseUrl(): ExtendedStorageReference {
        return storageRef(this.userBaseUrl(), "devices") as ExtendedStorageReference;
    }

    export function fontsBaseUrl(): ExtendedStorageReference {
        return storageRef(this.userBaseUrl(), "fonts") as ExtendedStorageReference;
    }

    export function deviceBaseUrl(optionalDeviceId?: string): ExtendedStorageReference {
        let deviceId = optionalDeviceId || UserDevice.current.id
        return storageRef(this.devicesBaseUrl(), deviceId) as ExtendedStorageReference;
    }

    export function projectBaseUrl(deviceId?: string): ExtendedStorageReference {
        return storageRef(this.deviceBaseUrl(deviceId), "projects") as ExtendedStorageReference;
    }

    export function projectUrl(projectId: string, deviceId?: string): ExtendedStorageReference {
        return storageRef(this.projectBaseUrl(deviceId), projectId) as ExtendedStorageReference;
    }

    export function projectScreenshotUrl(projectId: string, deviceId: string): ExtendedStorageReference {
        const relativeUrl = ScreenshotProvider.screenshotRelativeToProject(projectId); // Adjust according to your URL module
        return storageRef(this.projectBaseUrl(deviceId), relativeUrl) as ExtendedStorageReference;
    }

    export function foldersMetaData(): ExtendedStorageReference {
        return storageRef(this.userBaseUrl(), "foldersMetaData.json") as ExtendedStorageReference;
    }

    export function unsafeFeedMetaData(): ExtendedStorageReference {
        return storageRef(this.userBaseUrl(), "unsafeFeed.json") as ExtendedStorageReference;
    }

    export function deviceMetaData(device?: string): ExtendedStorageReference {
        return storageRef(this.deviceBaseUrl(device), "syncMetaData.json") as ExtendedStorageReference;
    }

    export function projectFilesList(projectId: string, device?: string): ExtendedStorageReference {
        return storageRef(this.projectBaseUrl(device), `${projectId}${projectFilesListSuffix}`) as ExtendedStorageReference;
    }

    export async function projectIdOfProjectFilesList(ref: ExtendedStorageReference): Promise<string | null> {
        const lastComponent = ref.fullPath.split("/").pop();
        return lastComponent ? lastComponent.replace(projectFilesListSuffix, "") : null;
    }

    export function create(projectsRelativePath: string, deviceId?: string): ExtendedStorageReference {
        return storageRef(this.projectBaseUrl(deviceId), projectsRelativePath) as ExtendedStorageReference;
    }

    export function lastComponent(ref: ExtendedStorageReference): string | null {
        return ref.fullPath.split("/").pop() || null;
    }

    export function relativeToProjectsPath(ref: ExtendedStorageReference, deviceId?: string): string {
        const base = this.projectBaseUrl(deviceId).fullPath + "/";
        return ref.fullPath.replace(base, "");
    }
    export function child(url: ExtendedStorageReference, relativePath: string): ExtendedStorageReference {
        return storageRef(url, relativePath) as ExtendedStorageReference;
    }
    export async function projectFileMetaData(
        cloudUrl: StorageReference,
        deviceId?: string
      ): Promise<CloudResponse<ProjectFileMetaData> | EmptyResponse> {
        let relativeUrl = this.relativeToProjectsPath(deviceId)
        return storageFetch.projectFileMetaData(relativeUrl)
    }

    export const exist = storageFetch.exist;
    export const deleteFile = storageFetch.deleteFile;
    export const deleteAllFiles = storageFetch.deleteAllFiles;
    export const listAll = storageFetch.listAll;
    export const downloadFile = storageFetch.downloadFile;
    export const get = storageFetch.get;
    export const getMetadata = storageFetch.getMetadata;
    export const updateMetadata = storageFetch.updateMetadata;

    export type DownloadTaskSnapshotSW = storageFetch.DownloadTaskSnapshotSW;
    export type StorageTask = storageFetch.StorageTask;

    export const putJson = storageFetch.putJson;
    export const putFile = storageFetch.putFile;
    export const putFileBlob = storageFetch.putFileBlob;
}
